import React, { useEffect, useState } from"react";
import { useNavigate } from"react-router-dom";
import { useTenant } from"../Context/TenantContext";
import { ArrowBack } from "@mui/icons-material";
import {
 Box,
 TextField,
 MenuItem,
 Select,
 InputLabel,
 FormControl,
 Button,
 Typography,
 IconButton,
} from"@mui/material";
import API_BASE_URL from"../Config/config";
import { useSubscription } from "../Context/SubscriptionContext";
import FeatureGuard from "./access/FeatureGuard";
import { updateLearningConsistency } from '../utils/consistency';
import { UserContext } from '../BasicComponents/UserContext';
import { useContext } from 'react';

/* ----------------------------- UI helpers ----------------------------- */
const sxField = {
  "& .MuiInputBase-root": { 
    backgroundColor: "#FFFFFF", 
    color: "var(--text-heading)", 
    borderRadius: 3, 
    fontWeight: 600,
    "& input": { color: "var(--text-heading)" }
  },
  "& .MuiFormLabel-root": { color: "var(--text-muted)", fontWeight: 600 },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--accent-gray)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "var(--primary-blue)" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "var(--primary-blue)" },
  "& svg": { color: "var(--primary-blue)" },
  "& .MuiSelect-select": { color: "var(--text-heading)" },
};

function SelectField({
 name,
 label,
 value,
 onChange,
 required = false,
 children,
 sx = sxField,
 placeholder ="— Select —",
}) {
 const labelId = `${name}-label`;
 const id = `${name}-select`;
 return (
 <FormControl required={required} fullWidth margin="normal" sx={sx}>
 <InputLabel id={labelId}>{label}</InputLabel>
 <Select
 labelId={labelId}
 id={id}
 name={name}
 value={value ??""}
 onChange={onChange}
 label={label}
 >
 <MenuItem value="" disabled>
 {placeholder}
 </MenuItem>
 {children}
 </Select>
 </FormControl>
 );
}

/* ------------------------------- data -------------------------------- */
const experienceLevels = [
"0-2 years",
"3-6 years",
"6-9 years",
"10-12 years",
"13-15 years",
"15+ years",
];

const singleTechOptions = [
"Java",
"Python",
"Cpp",
"Dart",
"Swift",
"GoLang",
"Rust",
"Kubernetes",
"Jenkins",
"Git & GitHub",
"SQL",
"MongoDB",
"JavaScript",
"AWS",
];

const techBunchOptions = [
"Java + Spring Boot + Hibernate + Microservices",
"Python + Django + REST API + PostgreSQL",
"JavaScript + React + Node.js + Express",
"HTML + CSS + JavaScript + Bootstrap/Tailwind",
"AWS + Docker + Kubernetes + Terraform",
"Azure + CI/CD + Helm + Monitoring (Grafana/Prometheus)",
"Linux + Shell Scripting + Git + Jenkins",
"SQL + PL/SQL + Data Warehousing + ETL Tools", 
"Python + NumPy + Pandas + Matplotlib/Seaborn",
"TensorFlow + Keras + PyTorch + Scikit-learn",
];

const roleOptions = [
 // 0-2 Years (Entry-Level/Freshers)
"Software Engineer / Developer",
"Frontend Developer",
"Backend Developer",
"Junior DevOps Engineer",
"QA/Automation Tester",
 
 // 3-6 Years (Intermediate)
"Full-Stack Engineer",
"Cloud Engineer",
"Data Engineer",
"Automation Architect (Junior)",
"AI/ML Engineer",
"Performance Engineer",
 
 // 6-9 Years (Advanced Technical Lead)
"Technical Lead",
"Solution Architect",
"Senior DevOps / SRE Engineer",
"AI/ML Specialist",
"Data Architect",
"Security Engineer",
 
 // 10-12 Years (Principal/Specialist Roles)
"Principal Engineer",
"Platform Architect",
"Cloud Solutions Architect",
"Integration Architect",
"AI/ML Architect",
"Performance Architect",
 
 // 13-15 Years (Enterprise-Level Technical Leadership)
"Enterprise Architect",
"Chief/Principal Solutions Architect",
"SRE/Observability Architect",
"DevSecOps Architect",
"Big Data Architect",
"Innovation Architect",
 
 // 15+ Years (Distinguished Technical Authority)
"Distinguished Engineer / Technology Fellow",
"Chief Architect",
"GenAI Solutions Architect",
"AI Infrastructure Architect",
"Cloud-Native Architect",
"Digital Transformation Technical Leader",
 
 // Legacy/Alternative names (for backward compatibility)
"AIML Architect",
"AIML Specialist", 
"Artificial intelligence Engineer",
"Automation Architect",
"QA Tester",
"Senior DevOps",
"Software Engineer",
];

const certifications = [
"AWS Certified Solutions Architect – Associate",
"Azure",
"CCNA",
"CKA",
"Flutter",
"IBM",
"ML",
"PMP",
"Security",
"TensorFlow",
];

/* ------------------------------ helpers ------------------------------ */
const requiredForTrack = (form) => {
  if (!form.githubUsername) return false; // mandatory for all
  switch (form.track) {
    case "technologies":
      return !!form.technology;
    case "techbunch":
      return !!form.techBunch;
    case "roles":
      return !!form.role && !!form.workMode;
    case "certified":
      return !!form.certification;
    default:
      return false;
  }
};
const onlyDigits = (s) => String(s ||"").replace(/[^\d]/g,"");
const enKey = (uid, track) =>
 `enrollment_last_${String(uid ||"")}_${String(track ||"").toLowerCase()}`;

/* --------------------------------- form -------------------------------- */

export default function EnrollmentForm() {
    return (
        <FeatureGuard feature="BOOTCAMP">
            <div className="bg-bgMain min-h-screen">
                <BootcampEnrollment />
            </div>
        </FeatureGuard>
    );
}

function BootcampEnrollment() {
 const { user } = useContext(UserContext);
 const navigate = useNavigate();
 const { tenant } = useTenant();
 const isMainPlatform = !tenant ||
   tenant.domain === 'theskillgenie.com' ||
   tenant.tenantKey === 'skillgenie' ||
   window.location.hostname === 'theskillgenie.com' ||
   window.location.hostname === 'www.theskillgenie.com' ||
   window.location.hostname === 'localhost';
 const [loading, setLoading] = useState(false);

 const [form, setForm] = useState(() => ({
 userId: localStorage.getItem("user_id") ||"",
 fullName:"",
 phone:"",
 githubUsername:"", // mandatory for all tracks
 dob:"",
 gender:"",
 address:"",
 track:"technologies",
 experienceLevel:"",
 technology:"",
 techBunch:"",
 role:"",
 workMode:"",
 certification:"",
 working:"",
 techOrNonTech:"",
 prevSkills:"",
 currentRole:"",
 company:"",
 experience:"",
 interestedRole:"",
 reason:"",
 taskLevel:"",
 taskPreference:"",
 areaOfInterest:"",
 }));
 const [errors, setErrors] = useState({});

 useEffect(() => {
    const checkExistingEnrollment = async () => {
      const storedUid =
        localStorage.getItem("user_id") ||
        (() => {
          try {
            const u = JSON.parse(localStorage.getItem("user") || "{}");
            return u.userId || u.user_id || "";
          } catch {
            return "";
          }
        })();

      if (storedUid) {
        try {
          const sessionToken = localStorage.getItem('session_token');
          const headers = {
            'Content-Type': 'application/json'
          };
          if (sessionToken) {
            headers['Authorization'] = `Bearer ${sessionToken}`;
            headers['X-Session-Token'] = sessionToken;
          }
          const res = await fetch(`${API_BASE_URL}/api/bootcamp/enrollments/my-enrollment?userId=${storedUid}`, {
            headers
          });
          if (res.ok) {
            const data = await res.json();
            if (data.ok && data.enrollment) {
              // User already enrolled, redirect to the Technology dashboard
              const enrollment = data.enrollment;
              let dashboardPath = `/techuserdashboard?enrollmentId=${enrollment.enrollment_id}&userId=${encodeURIComponent(storedUid)}`;
              
              navigate(dashboardPath);
            }
          }
        } catch (err) {
          console.error("Error checking enrollment:", err);
        }
      }
    };
    checkExistingEnrollment();
  }, [navigate]);

  useEffect(() => {
    const storedUid =
 localStorage.getItem("user_id") ||
 (() => {
 try {
 const u = JSON.parse(localStorage.getItem("user") ||"{}");
 return u.userId || u.user_id ||"";
 } catch {
 return"";
 }
 })();

 if (storedUid && storedUid !== form.userId) {
 setForm((p) => ({ ...p, userId: String(storedUid) }));
 }
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, []);

 const applyCachedForTrack = (prev, trackValue) => {
 try {
 const raw = localStorage.getItem(enKey(prev.userId, trackValue));
 if (!raw) return prev;
 const enr = JSON.parse(raw);
 return {
 ...prev,
 experienceLevel: enr.experienceLevel ||"",
 technology: trackValue ==="technologies" ? enr.technology ||"" :"",
 techBunch: trackValue ==="techbunch" ? enr.techBunch ||"" :"",
 role: trackValue ==="roles" ? enr.role ||"" :"",
 workMode: trackValue ==="roles" ? enr.workMode ||"" :"",
 certification:
 trackValue ==="certified" ? enr.certification ||"" :"",
 githubUsername: enr.githubUsername ||"",
 };
 } catch {
 return prev;
 }
 };

 const handleChange = (e) => {
 const { name, value } = e.target;
   if (name === "phone") {
     const val = onlyDigits(value).slice(0, 10);
     setForm((p) => ({ ...p, phone: val }));
     if (val.length === 10 && errors.phone) {
       setErrors(prev => ({ ...prev, phone: null }));
     }
     return;
   }
 if (name ==="githubUsername") {
 return setForm((p) => ({ ...p, githubUsername: value.trim() }));
 }
 if (name ==="track") {
 return setForm((p) => {
 const cleared = {
 ...p,
 track: value,
 technology:"",
 techBunch:"",
 role:"",
 workMode:"",
 certification:"",
 // githubUsername is now global for all tracks
 };
 return applyCachedForTrack(cleared, value);
 });
 }
 setForm((prev) => ({ ...prev, [name]: value }));
 };

 useEffect(() => {
   if (user?.id) {
     updateLearningConsistency(user.id, 'BOOTCAMP_VISIT', 'Bootcamp');
   }
 }, [user]);

 const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("[BootcampEnrollment] handleSubmit triggered. Form state:", form);
    console.log("[BootcampEnrollment] API_BASE_URL:", API_BASE_URL);

    const latestUserId =
      localStorage.getItem("user_id") ||
 form.userId ||
 (() => {
 try {
 const u = JSON.parse(localStorage.getItem("user") ||"{}");
 return u.userId || u.user_id ||"";
 } catch {
 return"";
 }
 })();

   if (!latestUserId) {
     alert("Missing user_id. Please login first.");
     return;
   }

   const newErrors = {};
   const phoneRegex = /^[0-9]{10}$/;
   if (!form.phone) {
     newErrors.phone = "Phone number is required";
   } else if (!phoneRegex.test(form.phone)) {
     newErrors.phone = "Please enter a valid 10-digit phone number";
   }

   if (Object.keys(newErrors).length > 0) {
     setErrors(newErrors);
     return;
   }

   if (!form.fullName || !form.phone || !form.track || !form.experienceLevel) {
 alert("Please fill Full Name, Phone, Track and Experience Level.");
 return;
 }
 if (!requiredForTrack(form)) {
  alert("Please complete all required fields, including your GitHub username.");
  return;
}

 // Extra frontend pattern check for GitHub (same as backend)
 if (!/^[a-zA-Z0-9-]{1,39}$/.test(form.githubUsername ||"")) {
 alert("Please provide a valid GitHub username (alphanumeric or hyphen, 1–39 chars).");
 return;
 }

    try {
      setLoading(true);
      const sessionToken = localStorage.getItem('session_token');
      const headers = {
        'Content-Type': 'application/json'
      };
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
        headers['X-Session-Token'] = sessionToken;
      }
      const requestUrl = `${API_BASE_URL}/api/bootcamp/enrollments`;
      const requestBody = { ...form, userId: String(latestUserId) };
      console.log(`[BootcampEnrollment] Sending POST request to ${requestUrl}`, requestBody);
      
      const res = await fetch(requestUrl, {
        method:"POST",
        headers,
        body: JSON.stringify(requestBody),
      });
      
      console.log(`[BootcampEnrollment] Response status: ${res.status}`);
      const enrollData = await res.json();
      console.log(`[BootcampEnrollment] Response data:`, enrollData);

      if (res.status === 409 || (enrollData && !enrollData.ok)) {
      alert(enrollData.error || enrollData.message || "Enrollment failed. You might already be registered.");
      setLoading(false);
      return;
    }
    if (!res.ok || !enrollData.ok) throw new Error(enrollData.error || `HTTP ${res.status}`);

    const { enrollment } = enrollData;
    try {
      localStorage.setItem(
        enKey(enrollment.user_id, enrollment.track),
        JSON.stringify(enrollment)
      );
      localStorage.removeItem("enrollment_last");
    } catch {}

    // Success feedback
    alert(
      "Enrollment Successful!\n\n" +
      "We are setting up your personalized GitHub repository.\n" +
      "Please check your email and GitHub account for the invitation.\n\n" +
      "Redirecting to your dashboard..."
    );

    // Redirect to the Technology dashboard for all tracks
    let dashboardPath = `/techuserdashboard?enrollmentId=${enrollment.enrollment_id}&userId=${encodeURIComponent(enrollment.user_id)}`;

    // Navigate to Dashboard
    navigate(dashboardPath);
  } catch (err) {
 alert(`Failed to submit enrollment: ${err.message}`);
 } finally {
 setLoading(false);
 }
 };

 const renderWorkingSection = () => (
 <>
 <SelectField
 name="working"
 label="Working Professional?"
 value={form.working}
 onChange={handleChange}
 >
 <MenuItem value="yes">Yes</MenuItem>
 <MenuItem value="no">No</MenuItem>
 </SelectField>

 {form.working ==="yes" && (
 <>
 <SelectField
 name="techOrNonTech"
 label="Technical / Non-Technical"
 value={form.techOrNonTech}
 onChange={handleChange}
 >
 <MenuItem value="tech">Tech</MenuItem>
 <MenuItem value="nonTech">Non Tech</MenuItem>
 </SelectField>
 <TextField
 fullWidth
 name="prevSkills"
 label="Previous Skills"
 value={form.prevSkills}
 onChange={handleChange}
 margin="normal"
 sx={sxField}
 />
 <TextField
 fullWidth
 name="currentRole"
 label="Current Role"
 value={form.currentRole}
 onChange={handleChange}
 margin="normal"
 sx={sxField}
 />
 <TextField
 fullWidth
 name="company"
 label="Company Name"
 value={form.company}
 onChange={handleChange}
 margin="normal"
 sx={sxField}
 />
 <TextField
 fullWidth
 name="experience"
 label="Experience (Years)"
 value={form.experience}
 onChange={handleChange}
 margin="normal"
 sx={sxField}
 />
 <TextField
 fullWidth
 name="interestedRole"
 label="Interested Role"
 value={form.interestedRole}
 onChange={handleChange}
 margin="normal"
 sx={sxField}
 />
 </>
 )}

 {form.working ==="no" && (
 <>
 <TextField
 fullWidth
 name="areaOfInterest"
 label="Area of Interest"
 value={form.areaOfInterest}
 onChange={handleChange}
 margin="normal"
 sx={sxField}
 />
 <TextField
 fullWidth
 name="reason"
 label="Reason to Register"
 value={form.reason}
 onChange={handleChange}
 margin="normal"
 sx={sxField}
 />
 <TextField
 fullWidth
 name="taskLevel"
 label="Level of Tasks Interested In"
 value={form.taskLevel}
 onChange={handleChange}
 margin="normal"
 sx={sxField}
 />
 <TextField
 fullWidth
 name="taskPreference"
 label="Preference of Task"
 value={form.taskPreference}
 onChange={handleChange}
 margin="normal"
 sx={sxField}
 />
 </>
 )}
 </>
 );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#E3F2FD", // Light Blue
        color: "var(--text-body)",
        py: 8,
        px: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: "100%",
          maxWidth: 750,
          backgroundColor: "#FFFFFF", // Form Box background white
          p: { xs: 3, md: 6 },
          borderRadius: 8,
          boxShadow: "0 20px 50px rgba(31, 138, 203, 0.15)",
          border: "1px solid var(--accent-gray)",
          position: "relative",
          overflow: "hidden"
        }}
      >
        <IconButton 
          onClick={() => navigate(-1)} 
          sx={{ 
            position: 'absolute', 
            top: 24, 
            left: 24, 
            backgroundColor: 'rgba(43, 176, 230, 0.1)',
            '&:hover': { backgroundColor: 'rgba(43, 176, 230, 0.2)' }
          }}
        >
          <ArrowBack sx={{ color: 'var(--primary-blue)' }} />
        </IconButton>
        {/* Background glow */}
        <Box sx={{ position: "absolute", top: -100, right: -100, width: 250, height: 250, borderRadius: "50%", background: "rgba(43, 176, 230, 0.05)", filter: "blur(60px)" }} />

        <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 900, color: "var(--text-heading)", textTransform: "uppercase", letterSpacing: "-0.02em", mb: 1 }}>
          Bootcamp <span style={{ color: "var(--primary-blue)" }}>Enrollment</span>
        </Typography>
        <Typography variant="h6" align="center" sx={{ mb: 6, color: "var(--text-muted)", fontWeight: 500 }}>
          Begin your journey into specialized industry training
        </Typography>

        <Box sx={{ position: "relative", zIndex: 1 }}>
          <TextField
            fullWidth
            label="User ID"
            margin="normal"
            value={form.userId}
            InputProps={{ readOnly: true }}
            helperText={
              form.userId ? "This is your username id from login" : "Not logged in"
            }
            InputLabelProps={{ shrink: true }}
            sx={sxField}
          />

          {/* 🔸 GitHub username ALWAYS visible and mandatory for all tracks */}
          <TextField
            required
            fullWidth
            name="githubUsername"
            label="GitHub Username"
            value={form.githubUsername}
            onChange={handleChange}
            margin="normal"
            sx={sxField}
            placeholder="e.g., torvalds"
            helperText="We collect this GitHub username to add you as a collaborator to our repositories for code submissions and project collaboration."
            FormHelperTextProps={{
              sx: { color: '#ffffff' }
            }}
          />

 <TextField
 required
 fullWidth
 name="fullName"
 label="Full Name"
 value={form.fullName}
 onChange={handleChange}
 margin="normal"
 sx={sxField}
 />
 <TextField
 required
 fullWidth
 name="phone"
 label="Phone Number"
 value={form.phone}
 onChange={handleChange}
 margin="normal"
 sx={sxField}
 error={!!errors.phone}
 helperText={errors.phone}
 />

 {/* Date, gender, address */}
 <TextField
 fullWidth
 type="date"
 name="dob"
 label="Date of Birth"
 value={form.dob}
 onChange={handleChange}
 margin="normal"
 sx={sxField}
 InputLabelProps={{ shrink: true }}
 />

 <SelectField
 name="gender"
 label="Gender"
 value={form.gender}
 onChange={handleChange}
 placeholder="— Select gender —"
 >
 <MenuItem value="Male">Male</MenuItem>
 <MenuItem value="Female">Female</MenuItem>
 <MenuItem value="Other">Other</MenuItem>
 </SelectField>

 <TextField
 fullWidth
 name="address"
 label="Address"
 value={form.address}
 onChange={handleChange}
 margin="normal"
 sx={sxField}
 multiline
 rows={2}
 />

 <SelectField
 required
 name="track"
 label="Select Skill Track"
 value={form.track}
 onChange={handleChange}
 placeholder="— Select track —"
 >
 <MenuItem value="technologies">Technology Mastery</MenuItem>
 {/* <MenuItem value="techbunch">Full Stack Bundles</MenuItem>
 <MenuItem value="roles">Industry Job Roles</MenuItem>
 <MenuItem value="certified">Global Certifications</MenuItem> */}
 </SelectField>

 <SelectField
 required
 name="experienceLevel"
 label="Level of Experience"
 value={form.experienceLevel}
 onChange={handleChange}
 placeholder="— Select experience —"
 >
 {experienceLevels.map((lvl) => (
 <MenuItem key={lvl} value={lvl}>
 {lvl}
 </MenuItem>
 ))}
 </SelectField>

 <TextField
 required
 fullWidth
 name="githubUsername"
 label="GitHub Username"
 value={form.githubUsername}
 onChange={handleChange}
 margin="normal"
 sx={sxField}
 placeholder="e.g., torvalds"
 helperText="Your GitHub username is required to provision your dedicated bootcamp repository and add you as a collaborator."
 FormHelperTextProps={{
 sx: { color: 'var(--text-muted)' }
 }}
 />

 {/* technologies */}
 {form.track ==="technologies" && (
 <>
 <SelectField
 required
 name="technology"
 label="Choose Technology"
 value={form.technology}
 onChange={handleChange}
 placeholder="— Select technology —"
 >
 {singleTechOptions.map((opt) => (
 <MenuItem key={opt} value={opt}>
 {opt}
 </MenuItem>
 ))}
 </SelectField>
 {renderWorkingSection()}
 </>
 )}

 {/* Other tracks hidden per requirement */}
 {/* techbunch */}
 {/* {form.track ==="techbunch" && (
 ...
 )} */}

 {/* roles */}
 {/* {form.track ==="roles" && (
 ...
 )} */}

 {/* certified */}
 {/* {form.track ==="certified" && (
 ...
 )} */}

          <Button
            fullWidth
            type="submit"
            disabled={loading}
            sx={{ 
              mt: 6,
              py: 2,
              fontWeight: 800,
              fontSize: "1.1rem",
              borderRadius: 3,
              backgroundColor: "var(--primary-blue)",
              color: "#fff",
              textTransform: "none",
              boxShadow: "0 10px 20px rgba(43, 176, 230, 0.3)",
              "&:hover": {
                backgroundColor: "var(--secondary-blue)",
                transform: "translateY(-1px)",
                boxShadow: "0 12px 25px rgba(31, 138, 203, 0.4)",
              },
              transition: "all 0.2s",
              "&.Mui-disabled": {
                background: "var(--accent-gray)",
                color: "var(--text-muted)"
              }
            }}
          >
            {loading ? "Processing..." : "Submit Enrollment Request"}
          </Button>
 </Box>
 </Box>
 </Box>
 );
}



